import Link from "next/link";
import FAIcon from "@/components/icons/faIcon";
import MenuFeatureBarLink from "@/components/menu/menuFeatureBarLink";

const ITEMS = [
  {
    title: "Manufacturing",
    description:
      "Practical consultancy for production sites, machinery safety and day-to-day compliance in fast-moving manufacturing environments.",
    href: "/sectors/manufacturing",
  },
  {
    title: "Logistics",
    description:
      "Support for warehousing, transport operations and busy distribution environments where safe systems need to hold up under pressure.",
    href: "/sectors/logistics",
  },
  {
    title: "Construction",
    description:
      "Clear guidance for site safety, contractor control and changing project risk across live construction environments.",
    href: "/sectors/construction",
  },
  {
    title: "Engineering",
    description:
      "Expert input for plant, equipment and technical operations where compliance needs to keep pace with output and maintenance demands.",
    href: "/sectors/engineering",
  },
  {
    title: "Retail",
    description:
      "Practical health and safety support for customer-facing teams, premises and back-of-house operations in retail settings.",
    href: "/sectors/retail",
  },
];

const sectorsMenuFeature = {
  eyebrow: "Our sectors",
  title: "Health and safety consultancy shaped around your environment.",
  description:
    "Explore how SML supports engineering, manufacturing, retail, construction and logistics organisations with practical sector-specific advice.",
  href: "/sectors",
  cta: "View all sectors",
  image: "/images/sectors/sector-logistics.webp",
  imagePosition: "center center",
  metrics: ["Engineering", "Manufacturing", "Logistics"],
};

const PANEL_WIDTH = 940;
const PANEL_SURFACE = "bg-neutral-50";
const PANEL_GLOW = "shadow-[0_34px_110px_rgba(6,4,12,0.18)]";
const DROPDOWN_SHADOW = "shadow-3xl shadow-blackq/20";

const FEATURED = sectorsMenuFeature;

function MenuArrowIcon({ className = "", noMargin = false, size }) {
  return (
    <FAIcon
      icon="arrow-right"
      className={className}
      noMargin={noMargin}
      size={size}
    />
  );
}

const LIGHT_CARD_SURFACE = "bg-white";

function DesktopDropdown({ id, onNavigate }) {
  return (
    <div
      id={id}
      style={{ width: `${PANEL_WIDTH}px`, maxWidth: "calc(100vw - 2.5rem)" }}
      className={`relative overflow-hidden rounded-lg p-8 ${PANEL_GLOW} ${DROPDOWN_SHADOW} ${PANEL_SURFACE}`}
    >
      <div className="space-y-6">
        <div className="grid gap-2 sm:grid-cols-2">
          {ITEMS.map((item, index) => {
            const shouldSpanFullRow =
              ITEMS.length % 2 !== 0 && index === ITEMS.length - 1;

            return (
              <Link
                key={item.title}
                href={item.href}
                onClick={onNavigate}
                className={`group flex h-full flex-col justify-between rounded-lg p-6 transition-all duration-300 hover:-translate-y-0.5 ${LIGHT_CARD_SURFACE} ${
                  shouldSpanFullRow ? "sm:col-span-2" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-3">
                    <h4 className="text-2xl font-medium leading-none tracking-[-0.05em] text-[#06040c]">
                      {item.title}
                    </h4>
                    <p className="max-w-xl text-sm leading-6 text-[#06040c]/64">
                      {item.description}
                    </p>
                  </div>
                  <MenuArrowIcon className="h-5 w-5 shrink-0 self-center text-[#06040c]/38 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#06040c]" />
                </div>
              </Link>
            );
          })}
        </div>

        <MenuFeatureBarLink
          href={FEATURED.href}
          onNavigate={onNavigate}
          description={FEATURED.description}
          cta={FEATURED.cta}
          descriptionClassName="max-w-[80%]"
          ctaTextClassName="max-w-32 font-semibold !text-[1.4rem]"
          buttonClassName="h-13 w-13"
        />
      </div>
    </div>
  );
}

function MobileContent({ onNavigate }) {
  return (
    <div className="space-y-1.5 pb-2.5">
      {ITEMS.map((item) => (
        <Link
          key={item.title}
          href={item.href}
          onClick={onNavigate}
          className="flex min-w-0 items-start justify-between gap-3 rounded-md bg-white px-3.5 py-2.5"
        >
          <div className="min-w-0">
            <p className="text-md font-semibold text-[#06040c]">{item.title}</p>
            <p className="mt-1 text-[0.84rem] leading-4.5 text-[#06040c]/64">
              {item.description}
            </p>
          </div>
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-[#06040c]">
            <MenuArrowIcon className="mx-auto" noMargin size={14} />
          </span>
        </Link>
      ))}

      <Link
        href={FEATURED.href}
        onClick={onNavigate}
        className="flex min-w-0 items-start justify-between gap-3 rounded-md bg-white px-3.5 py-2.5 text-[#06040c] transition-all duration-300 hover:-translate-y-0.5"
      >
        <div className="min-w-0">
          <p className="text-md font-semibold text-[#06040c]">{FEATURED.cta}</p>
          <p className="mt-1 text-[0.84rem] leading-4.5 text-[#06040c]/64">
            {FEATURED.description}
          </p>
        </div>
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-[#06040c]">
          <MenuArrowIcon className="mx-auto" noMargin size={14} />
        </span>
      </Link>
    </div>
  );
}

const sectorsMenu = {
  label: "Sectors",
  DesktopDropdown,
  MobileContent,
};

export default sectorsMenu;
